import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { RoteiroOperacionalAtivo } from '../../types/operacional';
import { Maximize2, RefreshCw, Compass, Layers, Phone, Battery, Gauge, MapPin, Radio } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface Props {
  roteiros: RoteiroOperacionalAtivo[];
  selectedRoteiroId?: number | null;
  onSelectRoteiro: (id: number) => void;
}

export const MapaMotoristasOperacional: React.FC<Props> = ({
  roteiros,
  selectedRoteiroId,
  onSelectRoteiro
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markersRef = useRef<{ [key: string]: L.Marker }>({});
  const polylineRef = useRef<L.Polyline[]>([]);

  const [mapType, setMapType] = useState<'streets' | 'satellite' | 'dark'>('streets');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [ultimoPingGPS, setUltimoPingGPS] = useState<string | null>(null);

  // Escuta atualizações de GPS em tempo real do Supabase Realtime
  useEffect(() => {
    const channel = supabase
      .channel(`mapa-rastreamento-realtime-${Date.now()}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'rastreamento_motorista'
        },
        (payload) => {
          const dados = payload.new as any;
          if (dados && dados.motorista_id && dados.latitude && dados.longitude) {
            setUltimoPingGPS(new Date().toLocaleTimeString('pt-BR'));
            
            // Atualiza o marcador do motorista se existir no mapa
            const marker = markersRef.current[`driver_${dados.motorista_id}`];
            if (marker) {
              marker.setLatLng([dados.latitude, dados.longitude]);
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Inicializa o mapa Leaflet
  useEffect(() => {
    if (!mapContainerRef.current) return;

    if (!mapInstanceRef.current) {
      // Coordenadas padrão de São Paulo
      const map = L.map(mapContainerRef.current, {
        center: [-23.55052, -46.633308],
        zoom: 11,
        zoomControl: false
      });

      L.control.zoom({ position: 'bottomright' }).addTo(map);
      mapInstanceRef.current = map;
    }

    const map = mapInstanceRef.current;

    // Remove camadas de azulejo (tile layers) antigas
    map.eachLayer((layer) => {
      if (layer instanceof L.TileLayer) {
        map.removeLayer(layer);
      }
    });

    // Define Tile Layer base
    let tileUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
    let attribution = '&copy; OpenStreetMap contributors';

    if (mapType === 'satellite') {
      tileUrl = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
      attribution = '&copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community';
    } else if (mapType === 'dark') {
      tileUrl = 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png';
      attribution = '&copy; CARTO';
    }

    L.tileLayer(tileUrl, { attribution, maxZoom: 19 }).addTo(map);

  }, [mapType]);

  // Atualiza Marcadores dos Motoristas e Paradas
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    // Limpa marcadores e polilinhas anteriores
    Object.values(markersRef.current).forEach((marker: any) => marker?.remove?.());
    markersRef.current = {};

    polylineRef.current.forEach(p => p.remove());
    polylineRef.current = [];

    if (roteiros.length === 0) return;

    const bounds = L.latLngBounds([]);

    roteiros.forEach((roteiro) => {
      const pos = roteiro.posicao_atual;
      if (!pos || !pos.lat || !pos.lng) return;

      const isSelected = selectedRoteiroId === roteiro.id;
      const isEmTransito = roteiro.status_motorista === 'em_transito';

      bounds.extend([pos.lat, pos.lng]);

      // 1. Cria Ícone Customizado HTML para o Motorista
      const corStatus = isEmTransito ? '#10B981' : '#F59E0B'; // Verde ou Amarelo
      const htmlIcon = `
        <div class="relative group cursor-pointer flex flex-col items-center">
          ${isEmTransito ? `<div class="absolute -inset-2 bg-emerald-500/30 rounded-full animate-ping"></div>` : ''}
          <div style="background-color: ${corStatus}; border-color: white;" class="relative z-10 flex items-center justify-center text-white font-bold text-xs px-2.5 py-1 rounded-full shadow-lg border-2 ring-2 ring-slate-900/20">
            <span class="mr-1">🚚</span> ${roteiro.veiculo_placa}
          </div>
          <div class="bg-slate-900 text-white text-[10px] font-semibold px-2 py-0.5 rounded shadow mt-0.5 whitespace-nowrap">
            ${roteiro.motorista_nome.split(' ')[0]}
          </div>
        </div>
      `;

      const driverIcon = L.divIcon({
        html: htmlIcon,
        className: 'custom-driver-marker',
        iconSize: [120, 50],
        iconAnchor: [60, 25]
      });

      const driverMarker = L.marker([pos.lat, pos.lng], { icon: driverIcon }).addTo(map);

      // Popup ao clicar no motorista
      const popupContent = `
        <div class="p-2 min-w-[240px] font-sans">
          <div class="flex items-center gap-2 border-b border-slate-200 pb-2 mb-2">
            <div class="w-8 h-8 rounded-full bg-red-600 text-white flex items-center justify-center font-bold text-sm">
              ${roteiro.motorista_nome.charAt(0)}
            </div>
            <div>
              <h4 class="font-bold text-slate-800 text-sm leading-tight">${roteiro.motorista_nome}</h4>
              <p class="text-xs text-slate-500">${roteiro.veiculo_modelo} • <span class="font-semibold text-slate-700">${roteiro.veiculo_placa}</span></p>
            </div>
          </div>
          <div class="grid grid-cols-2 gap-2 text-xs mb-3 bg-slate-50 p-2 rounded">
            <div>
              <span class="text-slate-400 block">Velocidade:</span>
              <span class="font-bold text-slate-700">${pos.velocidade_kmh || 0} km/h</span>
            </div>
            <div>
              <span class="text-slate-400 block">Bateria:</span>
              <span class="font-bold text-emerald-600">${pos.bateria_pct || 100}%</span>
            </div>
          </div>
          <p class="text-xs text-slate-600 mb-2 truncate"><strong>Local:</strong> ${pos.endereco_aproximado || 'Em Rota'}</p>
          <div class="flex items-center justify-between text-xs pt-1 border-t border-slate-100">
            <span class="text-slate-500 font-medium">Progresso: <strong>${roteiro.percentual_concluido}%</strong></span>
            <span class="text-slate-400">${roteiro.paradas_concluidas}/${roteiro.paradas_totais} paradas</span>
          </div>
        </div>
      `;

      driverMarker.bindPopup(popupContent);
      driverMarker.on('click', () => {
        onSelectRoteiro(roteiro.id);
      });

      markersRef.current[`driver_${roteiro.id}`] = driverMarker;

      // 2. Traça Polilinha da Rota e Ícones de Paradas
      const routePoints: [number, number][] = [[pos.lat, pos.lng]];

      roteiro.paradas.forEach((parada, idx) => {
        if (parada.latitude && parada.longitude) {
          routePoints.push([parada.latitude, parada.longitude]);
          bounds.extend([parada.latitude, parada.longitude]);

          const isConcluida = parada.status_parada === 'concluida';
          const isColeta = parada.tipo_parada === 'coleta';
          const bgCor = isConcluida ? '#94A3B8' : (isColeta ? '#2563EB' : '#059669');

          const stopHtml = `
            <div style="background-color: ${bgCor};" class="w-6 h-6 rounded-full text-white font-bold text-[11px] flex items-center justify-center border-2 border-white shadow-md">
              ${parada.ordem_sequencia}
            </div>
          `;

          const stopIcon = L.divIcon({
            html: stopHtml,
            className: 'custom-stop-marker',
            iconSize: [24, 24],
            iconAnchor: [12, 12]
          });

          const stopMarker = L.marker([parada.latitude, parada.longitude], { icon: stopIcon }).addTo(map);

          stopMarker.bindPopup(`
            <div class="p-1 font-sans text-xs">
              <span class="px-1.5 py-0.5 text-[10px] uppercase font-bold rounded ${isColeta ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'} mb-1 inline-block">
                Parada ${parada.ordem_sequencia} - ${isColeta ? 'Coleta' : 'Entrega'}
              </span>
              <p class="font-bold text-slate-800 text-sm mt-0.5">${parada.cliente_nome}</p>
              <p class="text-slate-500 mt-1">${parada.endereco}</p>
              <div class="mt-2 text-slate-600 font-semibold">Status: <span class="capitalize">${parada.status_parada.replace('_', ' ')}</span></div>
            </div>
          `);

          markersRef.current[`stop_${roteiro.id}_${idx}`] = stopMarker;
        }
      });

      // Desenha linha conectando a rota
      if (routePoints.length > 1) {
        const polyline = L.polyline(routePoints, {
          color: isSelected ? '#EF4444' : '#3B82F6',
          weight: isSelected ? 4 : 3,
          opacity: isSelected ? 0.9 : 0.6,
          dashArray: isSelected ? undefined : '6, 6'
        }).addTo(map);

        polylineRef.current.push(polyline);
      }
    });

    // Ajusta o zoom para mostrar todos os pontos
    if (bounds.isValid() && !selectedRoteiroId) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [roteiros, selectedRoteiroId]);

  // Se um roteiro for selecionado, centraliza no motorista
  useEffect(() => {
    if (!selectedRoteiroId || !mapInstanceRef.current) return;
    const roteiroSel = roteiros.find(r => r.id === selectedRoteiroId);
    if (roteiroSel && roteiroSel.posicao_atual) {
      mapInstanceRef.current.flyTo(
        [roteiroSel.posicao_atual.lat, roteiroSel.posicao_atual.lng],
        14,
        { duration: 1.2 }
      );

      // Abre popup do motorista
      const marker = markersRef.current[`driver_${selectedRoteiroId}`];
      if (marker) {
        marker.openPopup();
      }
    }
  }, [selectedRoteiroId, roteiros]);

  const handleFitAll = () => {
    const map = mapInstanceRef.current;
    if (!map || roteiros.length === 0) return;

    const bounds = L.latLngBounds([]);
    roteiros.forEach(r => {
      if (r.posicao_atual) bounds.extend([r.posicao_atual.lat, r.posicao_atual.lng]);
    });

    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  };

  const handleSimularAtualizacao = () => {
    setIsRefreshing(true);
    setTimeout(() => {
      setIsRefreshing(false);
    }, 600);
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-md overflow-hidden relative">
      {/* Barra de Controle Superior do Mapa */}
      <div className="p-3 bg-slate-900 text-white flex flex-wrap items-center justify-between gap-3 z-10 relative">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded bg-red-600/20 text-red-400 border border-red-500/30">
            <Compass className="h-5 w-5 animate-spin-slow" />
          </div>
          <div>
            <h3 className="font-bold text-sm leading-tight flex items-center gap-2">
              Monitoramento GPS em Tempo Real
              <span className="flex h-2 w-2 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
            </h3>
            <p className="text-[11px] text-slate-400 flex items-center gap-2">
              <span>{roteiros.length} motorista(s) ativo(s) em rota</span>
              {ultimoPingGPS && (
                <span className="text-emerald-400 font-mono text-[10px] bg-emerald-950/60 border border-emerald-500/30 px-1.5 py-0.2 rounded flex items-center gap-1">
                  <Radio className="w-2.5 h-2.5 animate-pulse" /> GPS: {ultimoPingGPS}
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Botões de Ação e Seletor de Camadas */}
        <div className="flex items-center gap-2">
          {/* Seletor de Tipo de Mapa */}
          <div className="bg-slate-800 p-1 rounded-lg border border-slate-700 flex text-xs">
            <button
              onClick={() => setMapType('streets')}
              className={`px-2.5 py-1 rounded-md transition font-medium ${mapType === 'streets' ? 'bg-red-600 text-white shadow' : 'text-slate-400 hover:text-white'}`}
            >
              Rua
            </button>
            <button
              onClick={() => setMapType('satellite')}
              className={`px-2.5 py-1 rounded-md transition font-medium ${mapType === 'satellite' ? 'bg-red-600 text-white shadow' : 'text-slate-400 hover:text-white'}`}
            >
              Satélite
            </button>
            <button
              onClick={() => setMapType('dark')}
              className={`px-2.5 py-1 rounded-md transition font-medium ${mapType === 'dark' ? 'bg-red-600 text-white shadow' : 'text-slate-400 hover:text-white'}`}
            >
              Escuro
            </button>
          </div>

          <button
            onClick={handleFitAll}
            title="Centralizar Todos no Mapa"
            className="p-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg text-slate-300 hover:text-white text-xs flex items-center gap-1 transition"
          >
            <Maximize2 className="h-4 w-4" />
            <span className="hidden sm:inline">Visão Geral</span>
          </button>

          <button
            onClick={handleSimularAtualizacao}
            title="Atualizar GPS Agora"
            className="p-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg text-slate-300 hover:text-white text-xs flex items-center gap-1 transition"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin text-emerald-400' : ''}`} />
          </button>
        </div>
      </div>

      {/* Container do Mapa Leaflet */}
      <div 
        ref={mapContainerRef} 
        className="w-full h-[450px] z-0 bg-slate-100"
      />

      {/* Legenda Flutuante do Mapa */}
      <div className="absolute bottom-4 left-4 z-10 bg-white/95 backdrop-blur-md p-2.5 rounded-xl border border-slate-200/80 shadow-lg text-xs space-y-1.5 hidden sm:block">
        <div className="font-bold text-slate-700 mb-1 border-b pb-1 text-[11px] uppercase tracking-wider">Legenda do Rastreamento</div>
        <div className="flex items-center gap-2 text-slate-600">
          <span className="w-3 h-3 rounded-full bg-emerald-500 inline-block shadow-sm"></span>
          <span>Motorista em Trânsito</span>
        </div>
        <div className="flex items-center gap-2 text-slate-600">
          <span className="w-3 h-3 rounded-full bg-amber-500 inline-block shadow-sm"></span>
          <span>Motorista em Parada / Cliente</span>
        </div>
        <div className="flex items-center gap-2 text-slate-600">
          <span className="w-3 h-3 rounded-full bg-blue-600 inline-block shadow-sm"></span>
          <span>Parada de Coleta</span>
        </div>
        <div className="flex items-center gap-2 text-slate-600">
          <span className="w-3 h-3 rounded-full bg-emerald-600 inline-block shadow-sm"></span>
          <span>Parada de Entrega</span>
        </div>
      </div>
    </div>
  );
};
